
import { Component, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ListboxModule } from 'primeng/listbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { SharePointItem, SharePointPage, SharePointService } from '../../../../../../../shared/services/sharepoint.service';

@Component({
  selector: 'app-sharepoint-button',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    DialogModule,
    ListboxModule,
    ProgressSpinnerModule,
    InputTextModule,
    TooltipModule
],
  templateUrl: './sharepoint-button.html',
  styleUrls: ['./sharepoint-button.scss']
})
export class SharepointButtonComponent {
  public readonly fileSelected = output();

  protected displayFileBrowser = false;
  protected loading = false;
  protected processing = false;

  // Replace-Modus: gesetzt wenn ein bestehendes Dokument ersetzt werden soll
  private replaceDocId: string | null = null;
  protected get isReplaceMode(): boolean { return this.replaceDocId !== null; }

  // Tab 1 - Dateibrowser
  protected currentFolderId = 'root';
  protected items: SharePointItem[] = [];
  protected breadcrumbs: { id: string, name: string }[] = [{ id: 'root', name: 'root' }];
  protected searchQuery = '';

  // Tab 2 - Web Pages
  protected pages: SharePointPage[] = [];
  protected pageSearchQuery = '';

  protected activeTab = 0;

  protected get btnLabel(): string {
    if (this.processing) return 'Verarbeiten…';
    return this.isReplaceMode ? 'SharePoint (Ersetzen)' : 'SharePoint';
  }

  protected get btnIcon(): string {
    return this.processing ? 'ri-loader-2-line spinning-icon' : 'ri-microsoft-line';
  }

  constructor(protected spService: SharePointService) { }

  /** Öffnet den Dialog im Replace-Modus für ein bestehendes Dokument */
  public openForReplace(docId: string) {
    this.replaceDocId = docId;
    void this.openFileBrowser();
  }

  protected get filteredItems(): SharePointItem[] {
    if (!this.searchQuery.trim()) return this.items;
    const q = this.searchQuery.toLowerCase();
    return this.items.filter(item => item.name.toLowerCase().includes(q));
  }

  protected get filteredPages(): SharePointPage[] {
    if (!this.pageSearchQuery.trim()) return this.pages;
    const q = this.pageSearchQuery.toLowerCase();
    return this.pages.filter(page => page.title.toLowerCase().includes(q));
  }

  protected getFileIcon(item: SharePointItem): string {
    if (item['isFolder']) return 'ri-folder-line';
    const ext = item.name.split('.').pop()?.toLowerCase() ?? '';
    const iconMap: Record<string, string> = {
      pdf: 'ri-file-pdf-line',
      doc: 'ri-file-word-line', docx: 'ri-file-word-line',
      xls: 'ri-file-excel-line', xlsx: 'ri-file-excel-line',
      ppt: 'ri-file-ppt-line', pptx: 'ri-file-ppt-line',
      png: 'ri-image-line', jpg: 'ri-image-line', jpeg: 'ri-image-line',
      gif: 'ri-image-line', svg: 'ri-image-line',
      mp4: 'ri-video-line', mov: 'ri-video-line',
      mp3: 'ri-music-line',
      zip: 'ri-file-zip-line', rar: 'ri-file-zip-line',
      txt: 'ri-file-text-line',
      html: 'ri-html5-line', htm: 'ri-html5-line'
    };
    return iconMap[ext] ?? 'ri-file-line';
  }

  // ---------- Öffnen des Popups ----------
  protected async openFileBrowser() {
    this.breadcrumbs = [{ id: 'root', name: 'home' }];
    this.displayFileBrowser = true;
    if (this.activeTab === 0) {
      await this.loadFolder('root');
    } else {
      await this.loadPages();
    }
  }

  // ---------- DATEIBROWSER ----------
  protected async loadFolder(folderId: string) {
    this.loading = true;
    this.searchQuery = '';
    try {
      this.currentFolderId = folderId;
      const data = await this.spService.readFolder(folderId);
      this.items = data.map(item => {
        item['isFolder'] = !!item.folder;
        return item;
      });
    } finally {
      this.loading = false;
    }
  }

  protected async selectItem(item: SharePointItem) {
    if (item['isFolder']) {
      this.breadcrumbs = [...this.breadcrumbs, { id: item.id, name: item.name }];
      await this.loadFolder(item.id);
    } else {
      this.displayFileBrowser = false;
      this.processing = true;
      const docId = this.replaceDocId;
      this.replaceDocId = null;
      try {
        if (docId) {
          await this.spService.replaceFile(item, docId);
        } else {
          await this.spService.selectFile(item);
        }
        // TODO: The 'emit' function requires a mandatory void argument
        this.fileSelected.emit();
      } finally {
        this.processing = false;
      }
    }
  }

  protected async navigateBreadcrumb(index: number) {
    // Stack auf den geklickten Eintrag kürzen, dann laden
    this.breadcrumbs = this.breadcrumbs.slice(0, index + 1);
    const folder = this.breadcrumbs[index];
    await this.loadFolder(folder ? folder.id : 'root');
  }

  // ---------- WEB PAGES ----------
  protected async loadPages() {
    this.loading = true;
    this.pageSearchQuery = '';
    try {
      this.pages = await this.spService.readPages();
    } finally {
      this.loading = false;
    }
  }

  protected async selectPage(page: SharePointPage) {
    this.displayFileBrowser = false;
    this.processing = true;
    const docId = this.replaceDocId;
    this.replaceDocId = null;
    try {
      if (docId) {
        await this.spService.replacePage(page, docId);
      } else {
        await this.spService.selectPage(page);
      }
      // TODO: The 'emit' function requires a mandatory void argument
      this.fileSelected.emit();
    } finally {
      this.processing = false;
    }
  }

  // ---------- Tab Wechsel ----------
  protected async onTabChange(event: { index: number }) {
    this.activeTab = event.index;
    if (this.activeTab === 0) {
      this.breadcrumbs = [{ id: 'root', name: 'home' }];
      await this.loadFolder('root');
    } else {
      await this.loadPages();
    }
  }
}
